import React, { useCallback, useEffect, useState } from 'react';
import MenuHeader from '../components/MenuHeader';
import { Box, Drawer, IconButton, SwipeableDrawer, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks';
import RestaurantInfo from '../components/RestaurantInfo';
import MenuItemsByCategory from '../components/MenuItemsByCategory';
import orderSlice from '../store/orderSlice';
import { ShippingMethod, PaymentMethod, TMenuItem, TOrder, TRestaurant, TWorkingHours } from '../types';
import StyledPaper from '../components/StyledPaper';
import StyledButton from '../components/StyledButton';
import { t } from '@lingui/macro';
import appRoutes from '../utils/routes';
import PriceRender from '../components/PriceRender';
import dayjs from 'dayjs';
import api from '../api';
import ComponentSelector from '../components/ComponentSelector';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../components/Loader';
import { InView } from 'react-intersection-observer';
import MenuSidebar from '../components/MenuSidebar';
import MenuCategoriesPicker from '../components/MenuCategoriesPicker';
import lodash from 'lodash';
import { FlashOnRounded } from '@mui/icons-material';
import ReviewsScreen from './ReviewsScreen';

const MenuFooter: React.FC<{ orderInfo: Partial<TOrder>, restaurantInfo: TRestaurant, orderActive?: boolean }> = (props) => {
    const { orderInfo, restaurantInfo, orderActive } = props

    const labels = {
        [ShippingMethod.delivery]: t`Delivery`,
        [ShippingMethod.takeout]: t`Takeaway`,
        [ShippingMethod.dineIn]: t`Dine-in`,
    }
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const shippingMethod = ShippingMethod[orderInfo.shippingMethod as keyof typeof ShippingMethod];
    const onCheckoutOrderClick = useCallback(
        async () => {
            if (orderInfo.paymentMethod == undefined) {
                const value = PaymentMethod[restaurantInfo!.paymentMethods[0] as keyof typeof PaymentMethod];
                dispatch(orderSlice.actions.changePaymentMethod(value))
            }

            if (orderInfo.shippingMethod == undefined) {
                const value = ShippingMethod[restaurantInfo!.shippingMethods[0] as keyof typeof ShippingMethod];
                dispatch(orderSlice.actions.changeShippingMethod(value))
            }

            if (orderInfo.deadlineDate == undefined || orderInfo.deadlineDate < new Date()) {
                dispatch(orderSlice.actions.changeDeadlineDate(new Date()))
            }

            navigate(appRoutes.checkout)
        }, []
    );
    return <StyledPaper
        sx={{ position: 'sticky', bottom: '0', height: '54px', padding: '16px 24px', display: 'flex', margin: '0 -24px' }}
    >
        {
            orderActive ? <>
                <Box display='flex' flexDirection={'column'} flex={1} alignItems={'start'} justifyContent={'center'}>
                    <Typography>{`${labels[shippingMethod]}, ` +
                        (orderInfo.shippingMethod === ShippingMethod.takeout
                            ? restaurantInfo?.address
                            : orderInfo.deliveryLocation?.address.split(',')[0])
                    }
                    </Typography>
                    <Typography>{t`at` +
                        ` ${dayjs(orderInfo.deadlineDate).format('HH:mm')}-${dayjs(orderInfo.deadlineDate).add(15, 'minute').format('HH:mm')}`
                    }</Typography>
                </Box>
                <Link style={{ height: '100%' }} to={appRoutes.orderStatus} >
                    <StyledButton sx={{ height: '100%' }} variant='contained'>
                        <Typography>{t`See info`}</Typography>
                    </StyledButton>
                </Link>
            </>
                : <>
                    <Box display='flex' flex={1} alignItems={'center'} justifyContent={'center'}>
                        <Typography>{t`${orderInfo.items?.length && orderInfo.items.reduce((acc, item) => acc + item.orderCount, 0)} meals`}</Typography>
                    </Box>
                    <Box pr='8px' />
                    <StyledButton sx={{ height: '100%' }} variant='contained' onClick={onCheckoutOrderClick}>
                        <Box mr={'8px'}>{t`Checkout`}</Box>
                        <PriceRender price={orderInfo!.total!} />
                    </StyledButton>
                </>
        }
    </StyledPaper>
}

const MenuScreen: React.FC = () => {
    type CategoryScroll = {
        id: string;
        isVisible: boolean;
    }

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const menuCategories = useAppSelector(state => state.restaurant.menuItems);
    const restaurantInfo = useAppSelector(state => state.restaurant.info);
    const orderInfo = useAppSelector(state => state.order);
    const isOrderActive = useAppSelector(state => state.user.hasCurrentOrder);

    const [workingHoursForToday, setWorkingHoursForToday] = useState<TWorkingHours | undefined>()
    const [activeItem, setActiveItem] = useState<TMenuItem | undefined>()
    const [showSidebar, setShowSidebar] = useState<boolean>(false)
    const [showReviews, setShowReviews] = useState<boolean>(false)
    const [categoriesActive, setCategoriesActive] = useState<boolean>(false)
    const [cats, setCats] = useState<CategoryScroll[]>([])
    const { hash: currentCategoryId } = useLocation();


    const onAddClick = useCallback(
        (item: TMenuItem) => {
            if (isOrderActive) return;
            if (item.components.length > 0) {
                setActiveItem(item)
            } else {
                dispatch(orderSlice.actions.addItemToOrder({ ...item }))
            }
        },
        [dispatch, isOrderActive]);

    const onShowClick = useCallback(
        (item: TMenuItem) => {
            if (isOrderActive) return;
            setActiveItem(item)
        },
        [dispatch, isOrderActive]);

    const onAddItemsWithComponentClick = (item: TMenuItem) => {
        setActiveItem(undefined)
        dispatch(orderSlice.actions.addItemToOrder({ ...item }))
    }

    const onRemoveClick = useCallback(
        (item: TMenuItem) => dispatch(orderSlice.actions.removeItemFromOrder(item)),
        [dispatch]);

    useEffect(() => {
        document.title = restaurantInfo?.name ?? ''
        !menuCategories?.length && !restaurantInfo && navigate(appRoutes.start)
    }, [navigate, menuCategories, restaurantInfo])
    useEffect(() => { api.getWorkingHoursForToday().then(data => setWorkingHoursForToday(data)) }, [])
    useEffect(() => { window.scrollTo({ top: 0 }); setTimeout(() => setCategoriesActive(true), 300) }, [])

    useEffect(() => {
        if (cats.length === 0) {
            const newarr = menuCategories?.map((val) => {
                return { id: val._id, isVisible: true } as CategoryScroll;
            }) as CategoryScroll[]
            setCats(newarr!)
            if (newarr) {
                const value = newarr[0].id
                if (value) {
                    navigate('#' + value, { replace: true })
                }
            }
        }
    }, [menuCategories])

    return <Box>
        {menuCategories?.length && restaurantInfo
            ? <>
                <MenuHeader restaurantName={restaurantInfo.name} onIconButtonClick={() => { setShowSidebar(true) }} />
                <Box>
                    <Box pb={'12px'} />
                    <RestaurantInfo restaurant={restaurantInfo} workingHours={workingHoursForToday} onReviewsClick={() => {
                        setShowReviews(true)
                    }} />
                    <Box pb={'12px'} />
                    <MenuCategoriesPicker
                        menuCategories={menuCategories}
                        onCategoryChange={() => {
                            //setCategoriesActive(false)
                            // setTimeout(() => setCategoriesActive(true), 300)
                        }}
                    />
                    <Box pb={'12px'} />
                    {menuCategories.map(
                        (category, index) => <InView
                            as="div"
                            key={category.categoryName}
                            rootMargin='-20% 0px 0px 0px'
                            threshold={0}
                            delay={0}
                            onChange={(inView, entry) => {

                                if (inView) {
                                    const value = cats.filter((val) => {
                                        return val.id === category._id
                                    })[0]

                                    if (value) {
                                        value.isVisible = true
                                    }
                                }

                                if (!inView) {
                                    const value = cats.filter((val) => {
                                        return val.id === category._id
                                    })[0]

                                    if (value) {
                                        value.isVisible = false
                                    }
                                }

                                const scrollToItemId = cats.filter((val) => {
                                    return val.isVisible == true
                                })[0]

                                if (scrollToItemId && (scrollToItemId.id !== currentCategoryId.substring(1))) {
                                    lodash.throttle(() => categoriesActive && navigate('#' + scrollToItemId.id, { replace: true }), 1000)()

                                }
                            }}>
                            <MenuItemsByCategory
                                orderItems={orderInfo.items || []}
                                category={category}
                                onShowClick={onShowClick}
                                onAddClick={onAddClick}
                                onRemoveClick={onRemoveClick}
                                isOrderActive={isOrderActive}
                            />
                        </InView>)}
                    {orderInfo?.items?.length > 0 && <MenuFooter orderInfo={orderInfo} restaurantInfo={restaurantInfo} orderActive={isOrderActive} />}
                </Box>
                <section id={'modals'}>
                    <SwipeableDrawer
                        anchor={'bottom'}
                        open={Boolean(activeItem)}
                        onClose={() => { setActiveItem(undefined); }}
                        onOpen={(event: React.SyntheticEvent<{}, Event>) => { }}
                    >
                        <Box display={'flex'} justifyContent={'end'}>
                            <IconButton size={'large'} onClick={() => { setActiveItem(undefined) }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        {activeItem && <ComponentSelector item={activeItem} onFinishSelection={onAddItemsWithComponentClick} />}
                    </SwipeableDrawer>
                    <SwipeableDrawer
                        PaperProps={{
                            sx: {
                                backgroundColor: "#F7F7F7",
                            }
                        }}
                        anchor={'bottom'}
                        open={showReviews}
                        onClose={() => { setShowReviews(false) }}
                        onOpen={(event: React.SyntheticEvent<{}, Event>) => { }}
                    >
                        <Box display={'flex'}
                            flexDirection={'row'}
                            position={'sticky'}
                            alignItems={'center'}
                            top={0}
                            sx={{
                                borderRadius: '12px',
                                zIndex: 1,
                                px: '8px',
                                color: '#F7F7F7',
                                background: '#F7F7F7',
                                margin: '0 -10px'
                            }}
                            justifyContent={'end'}>
                            <IconButton size={'large'} onClick={() => { setShowReviews(false) }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <ReviewsScreen />
                    </SwipeableDrawer>
                    <Drawer
                        anchor={'right'}
                        open={showSidebar}
                        onClose={() => { setShowSidebar(false) }}
                    >
                        <Box display={'flex'} justifyContent={'end'}>
                            <IconButton size={'large'} onClick={() => { setShowSidebar(false) }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box p={'0 20px 20px 20px'} width={'75vw'}>
                            <MenuSidebar />
                        </Box>
                    </Drawer>
                </section>
            </>
            : <Loader />}
    </Box>
}
export default MenuScreen;